/* eslint-disable react-refresh/only-export-components */
import { cn } from '@/lib/utils';
import type {
  Cell,
  Column,
  ColumnDef,
  Header,
  HeaderGroup,
  Row,
  SortingState,
  Table as TanstackTable,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { HTMLAttributes, ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef, ReactElement } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type TableState = {
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
};

export const useTableStore = create<TableState>()(
  devtools((set) => ({
    sorting: [],
    setSorting: (sorting: SortingState) => set(() => ({ sorting })),
  }))
);

export const TableContext = createContext<{
  data: unknown[];
  columns: ColumnDef<unknown, unknown>[];
  table: TanstackTable<unknown> | null;
}>({
  data: [],
  columns: [],
  table: null,
});

// True while a row's actions are drawn inside a card: the "..." menu becomes a row of labelled buttons.
const CardActionsContext = createContext(false);

export const Table = ({ className, ...props }: HTMLAttributes<HTMLTableElement>) => (
  <table className={cn('min-w-full', className)} {...props} />
);
Table.displayName = "Table";

export const TableHeader = ({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) => (
  <thead className={cn('bg-primary', className)} {...props} />
);
TableHeader.displayName = "TableHeader";

export const TableBody = ({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody className={cn('bg-white divide-y divide-gray-200', className)} {...props} />
);
TableBody.displayName = "TableBody";

export const TableRow = ({ className, onClick, ...props }: HTMLAttributes<HTMLTableRowElement>) => (
  <tr 
    className={cn('transition-colors border-b border-gray-200', onClick && 'cursor-pointer', className)} 
    onClick={onClick}
    {...props} 
  />
);
TableRow.displayName = "TableRow";

export const TableCell = ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
  <td className={cn('px-3 sm:px-6 py-2 text-xs whitespace-nowrap', className)} {...props} />
);
TableCell.displayName = "TableCell";

export const TableHead = ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
  <th className={cn('px-3 sm:px-6 py-2 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap', className)} {...props} />
);
TableHead.displayName = "TableHead";

export const TableActions = ({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const inCard = useContext(CardActionsContext);
  if (inCard) return <div className="w-full">{children}</div>;
  return (
    <div className={cn('px-3 sm:px-6 py-2 text-right text-xs whitespace-nowrap', className)} {...props}>
      <div className="flex justify-end">
        {children}
      </div>
    </div>
  );
};
TableActions.displayName = "TableActions";

export const TableActionsHead = ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
  <th className={cn('px-3 sm:px-6 py-2 text-right text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap', className)} {...props} />
);
TableActionsHead.displayName = "TableActionsHead";

export const TableDropdown = ({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const inCard = useContext(CardActionsContext);
  if (inCard) return <div className="flex flex-wrap gap-2">{children}</div>;
  return (
    <div className={cn('relative inline-block', className)} {...props}>
      {children}
    </div>
  );
};
TableDropdown.displayName = "TableDropdown";

export const TableDropdownButton = ({ className, ...props }: HTMLAttributes<HTMLButtonElement> & { type?: 'button' | 'submit' | 'reset' }) => {
  const inCard = useContext(CardActionsContext);
  if (inCard) return null;
  return (
    <button
      type="button"
      className={cn('inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-black hover:bg-gray-100 transition-colors', className)}
      {...props}
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
        <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
      </svg>
    </button>
  );
};
TableDropdownButton.displayName = "TableDropdownButton";

interface TableDropdownPopupProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
}

const TableDropdownPopup = ({ 
  className, 
  isOpen, 
  onClose, 
  position,
  children, 
  ...props 
}: TableDropdownPopupProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const rect = dropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      if (rect.bottom > viewportHeight) {
        dropdown.style.top = `${position.top - rect.height - 40}px`;
      }

      if (rect.right > viewportWidth) {
        dropdown.style.left = `${viewportWidth - rect.width - 16}px`;
      }
    }
  }, [isOpen, position]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className={cn('fixed z-[100] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px] animate-scale-in', className)}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      {...props}
    >
      {children}
    </div>
  );
};
TableDropdownPopup.displayName = "TableDropdownPopup";

export const TableDropdownMenu = ({ 
  className, 
  isOpen, 
  onClose, 
  position = { top: 0, left: 0 },
  children, 
  ...props 
}: TableDropdownPopupProps) => {
  const inCard = useContext(CardActionsContext);
  if (inCard) return <>{children}</>;
  return (
    <TableDropdownPopup
      isOpen={isOpen}
      onClose={onClose}
      position={position}
      className={className}
      {...props}
    >
      {children}
    </TableDropdownPopup>
  );
};
TableDropdownMenu.displayName = "TableDropdownMenu";

export const TableDropdownItem = ({ className, children, ...props }: HTMLAttributes<HTMLButtonElement> & { type?: 'button' | 'submit' | 'reset' }) => {
  const inCard = useContext(CardActionsContext);
  return (
    <button
      type="button"
      className={
        inCard
          ? cn(
              'flex flex-1 basis-[7.5rem] items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors',
              'first:border-primary first:bg-primary first:text-white first:hover:bg-primary/90',
              className
            )
          : cn('flex items-center gap-3 w-full text-left px-4 py-3 text-xs text-gray-700 hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg border-b border-gray-100 last:border-b-0', className)
      }
      {...props}
    >
      {children}
    </button>
  );
};
TableDropdownItem.displayName = "TableDropdownItem";

export const useTableDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    
    const viewportHeight = window.innerHeight;
    const shouldShowAbove = rect.bottom > viewportHeight / 1.5;
    
    const left = rect.right - 160;
    const top = shouldShowAbove ? rect.top - 10 : rect.bottom + 4;

    setPosition({ top, left });
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    position,
    handleOpen,
    handleClose,
  };
};

export type TableProviderProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  children: ReactNode;
  className?: string;
  /** Show the "Add row" button under the table. Omit to hide it. */
  onAddRow?: () => void;
  addRowLabel?: string;
  /** Row checkboxes and the floating selection bar. On by default. */
  selectable?: boolean;
  /** Optional bulk action shown in the selection bar. */
  onDeleteSelected?: (rows: TData[]) => void;
};

const exportRowsToCsv = (rows: Record<string, unknown>[]) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]).filter((k) => rows.some((r) => ['string', 'number', 'boolean'].includes(typeof r[k])));
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [keys.join(','), ...rows.map((r) => keys.map((k) => esc(r[k])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const SelectBox = ({ checked, indeterminate, onChange, label }: {
  checked: boolean; indeterminate?: boolean; onChange: (v: boolean) => void; label: string;
}) => (
  <input
    type="checkbox"
    aria-label={label}
    checked={checked}
    ref={(el) => { if (el) el.indeterminate = !!indeterminate; }}
    onChange={(e) => onChange(e.target.checked)}
    onClick={(e) => e.stopPropagation()}
    className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
  />
);

export function TableProvider<TData, TValue>({
  columns,
  data,
  children,
  className,
  onAddRow,
  addRowLabel = 'Add row',
  selectable = true,
  onDeleteSelected,
}: TableProviderProps<TData, TValue>) {
  const { sorting, setSorting } = useTableStore();
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const allColumns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!selectable) return columns;
    const selectColumn: ColumnDef<TData, TValue> = {
      id: '__select',
      enableSorting: false,
      header: ({ table }) => (
        <SelectBox
          label="Select all rows"
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={(v) => table.toggleAllRowsSelected(v)}
        />
      ),
      cell: ({ row }) => (
        <SelectBox label="Select row" checked={row.getIsSelected()} onChange={(v) => row.toggleSelected(v)} />
      ),
    };
    return [selectColumn, ...columns];
  }, [columns, selectable]);

  // Columns that may be dropped when the screen is too narrow, least important first.
  // A column opts in with `meta: { hideOrder: n }` (lower n = dropped first).
  const removableIds = React.useMemo(
    () =>
      allColumns
        .map((col) => ({
          id: (col.id ?? (col as { accessorKey?: string }).accessorKey) as string | undefined,
          order: (col.meta as { hideOrder?: number } | undefined)?.hideOrder,
        }))
        .filter((col): col is { id: string; order: number } => !!col.id && typeof col.order === 'number')
        .sort((a, b) => a.order - b.order)
        .map((col) => col.id),
    [allColumns]
  );

  // The table never scrolls sideways: while it does not fit it drops one removable column at a
  // time, and once none are left it turns into cards.
  const containerRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [hiddenSteps, setHiddenSteps] = useState(0);
  const [cardMode, setCardMode] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [prevData, setPrevData] = useState(data);

  const resetLayout = useCallback(() => {
    setHiddenSteps(0);
    setCardMode(false);
    setEpoch((e) => e + 1);
  }, []);

  if (prevData !== data) {
    setPrevData(data);
    setHiddenSteps(0);
    setCardMode(false);
    setEpoch((e) => e + 1);
  }

  const columnVisibility = React.useMemo<VisibilityState>(
    () => Object.fromEntries(removableIds.slice(0, hiddenSteps).map((id) => [id, false])),
    [removableIds, hiddenSteps]
  );

  const table = useReactTable({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: selectable,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
    },
  });

  // Re-evaluate from scratch whenever the available width changes (or web fonts finish loading).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let lastWidth = el.clientWidth;
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        const width = el.clientWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        resetLayout();
      });
      observer.observe(el);
    }
    document.fonts?.ready.then(resetLayout);
    return () => observer?.disconnect();
  }, [resetLayout]);

  // Runs before paint, so the table settles on a layout that fits without any visible flicker.
  useLayoutEffect(() => {
    if (cardMode) return;
    const wrap = tableWrapRef.current;
    if (!wrap || wrap.scrollWidth <= wrap.clientWidth + 1) return;
    if (hiddenSteps < removableIds.length) setHiddenSteps(hiddenSteps + 1);
    else setCardMode(true);
  }, [cardMode, hiddenSteps, epoch, removableIds.length]);

  const selectedRows = table.getSelectedRowModel().rows;

  return (
    <TableContext.Provider
      value={{
        data,
        columns: allColumns as never,
        table: table as never,
      }}
    >
      <div ref={containerRef}>
        {cardMode ? (
          <div data-card-view className="space-y-3 py-3">
            {table.getRowModel().rows.map((row) => {
              const cells = row.getAllCells();
              const detailCells = cells.filter(
                (cell) => cell.column.id !== '__select' && cell.column.id !== 'actions'
              );
              const [titleCell, ...restCells] = detailCells;
              const actionsCell = cells.find((cell) => cell.column.id === 'actions');

              return (
                <div
                  key={row.id}
                  className={cn(
                    'rounded-2xl border bg-white p-3.5',
                    row.getIsSelected() ? 'border-primary bg-primary/5' : 'border-gray-200'
                  )}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    {selectable && (
                      <div className="pt-0.5">
                        <SelectBox label="Select row" checked={row.getIsSelected()} onChange={(v) => row.toggleSelected(v)} />
                      </div>
                    )}
                    <div className="min-w-0 break-words">
                      {titleCell && flexRender(titleCell.column.columnDef.cell, titleCell.getContext())}
                    </div>
                  </div>

                  {restCells.length > 0 && (
                    <dl className="mt-3 grid grid-cols-1 gap-y-2.5 border-t border-gray-100 pt-3 sm:grid-cols-2 sm:gap-x-4">
                      {restCells.map((cell) => {
                        const header = cell.column.columnDef.header;
                        return (
                          <div key={cell.id} className="min-w-0">
                            <dt className="text-[11px] font-medium text-gray-500">
                              {typeof header === 'string' ? header : cell.column.id}
                            </dt>
                            <dd className="mt-0.5 break-words">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </dd>
                          </div>
                        );
                      })}
                    </dl>
                  )}

                  {actionsCell && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <CardActionsContext.Provider value={true}>
                        {flexRender(actionsCell.column.columnDef.cell, actionsCell.getContext())}
                      </CardActionsContext.Provider>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div ref={tableWrapRef} className="overflow-x-hidden">
            <Table className={className}>{children}</Table>
          </div>
        )}
      </div>

      {onAddRow && (
        cardMode ? (
          <button
            type="button"
            onClick={onAddRow}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary/90 transition-colors"
          >
            <span className="text-lg leading-none">+</span> {addRowLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={onAddRow}
            className="flex w-full items-center gap-2 px-6 py-2.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-primary/10 border-t border-gray-200 transition-colors"
          >
            <span className="text-base leading-none">+</span> {addRowLabel}
          </button>
        )
      )}

      {selectable && selectedRows.length > 0 && (
        <div className="sticky bottom-3 z-20 flex justify-center px-3 pointer-events-none">
          <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2 shadow-xl border border-gray-100 text-xs">
            <span className="text-gray-900 font-medium">
              {selectedRows.length} <span className="text-gray-500 font-normal">of {table.getRowModel().rows.length} selected</span>
            </span>
            {!table.getIsAllRowsSelected() && (
              <button type="button" onClick={() => table.toggleAllRowsSelected(true)} className="text-primary hover:underline">
                Select all
              </button>
            )}
            <span className="h-4 w-px bg-gray-200" />
            <button
              type="button"
              onClick={() => exportRowsToCsv(selectedRows.map((r) => r.original as Record<string, unknown>))}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Export CSV
            </button>
            {onDeleteSelected && (
              <button
                type="button"
                onClick={() => { onDeleteSelected(selectedRows.map((r) => r.original as TData)); setRowSelection({}); }}
                className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
              >
                Delete {selectedRows.length}
              </button>
            )}
            <button
              type="button"
              onClick={() => setRowSelection({})}
              className="text-gray-400 hover:text-gray-900 px-1"
              aria-label="Clear selection"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </TableContext.Provider>
  );
}

export type DataTableHeadProps = {
  header: Header<unknown, unknown>;
  className?: string;
};

export const DataTableHead = ({ header, className }: DataTableHeadProps) => (
  <TableHead key={header.id} className={className}>
    {header.isPlaceholder
      ? null
      : flexRender(header.column.columnDef.header, header.getContext())}
  </TableHead>
);

export type DataTableHeaderGroupProps = {
  headerGroup: HeaderGroup<unknown>;
  children: (props: { header: Header<unknown, unknown> }) => ReactNode;
};

export const DataTableHeaderGroup = ({
  headerGroup,
}: DataTableHeaderGroupProps) => (
  <TableRow key={headerGroup.id}>
    {headerGroup.headers.map((header) => (
      <DataTableHead key={header.id} header={header} />
    ))}
  </TableRow>
);

export type DataTableHeaderProps = {
  className?: string;
  children: (props: { headerGroup: HeaderGroup<unknown> }) => ReactNode;
};

export const DataTableHeader = ({ className, children }: DataTableHeaderProps) => {
  const { table } = useContext(TableContext);

  return (
    <TableHeader className={className}>
      {table?.getHeaderGroups().map((headerGroup) =>
        React.cloneElement(children({ headerGroup }) as ReactElement, { key: headerGroup.id })
      )}
    </TableHeader>
  );
};

export interface TableColumnHeaderProps<TData, TValue>
  extends HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
}

export function TableColumnHeader<TData, TValue>({
  title,
  className,
}: TableColumnHeaderProps<TData, TValue>) {
  return <div className={cn('text-xs whitespace-nowrap', className)}>{title}</div>;
}

export type DataTableCellProps = {
  cell: Cell<unknown, unknown>;
  className?: string;
};

export const DataTableCell = ({ cell, className }: DataTableCellProps) => (
  <TableCell className={className}>
    {flexRender(cell.column.columnDef.cell, cell.getContext())}
  </TableCell>
);

export type DataTableRowProps = {
  row: Row<unknown>;
  children: (props: { cell: Cell<unknown, unknown> }) => ReactNode;
  className?: string;
  onClick?: () => void;
};

export const DataTableRow = ({ row, children, className, onClick }: DataTableRowProps) => (
  <TableRow
    key={row.id}
    data-state={row.getIsSelected() && 'selected'}
    className={cn(
      row.getIsSelected() && 'bg-primary/10 shadow-[inset_3px_0_0_0_theme(colors.primary)]',
      className
    )}
    onClick={onClick}
  >
    {row.getVisibleCells().map((cell) =>
      React.cloneElement(children({ cell }) as ReactElement, { key: cell.id })
    )}
  </TableRow>
);

export type DataTableBodyProps = {
  children: (props: { row: Row<unknown> }) => ReactNode;
  className?: string;
};

export const DataTableBody = ({ children, className }: DataTableBodyProps) => {
  const { table } = useContext(TableContext);
  const rows = table?.getRowModel().rows;

  return (
    <TableBody className={className}>
      {rows?.map((row) => React.cloneElement(children({ row }) as ReactElement, { key: row.id }))}
    </TableBody>
  );
};